<template>
  <div class="target-gen-config">
    <!-- 基础设置 -->
    <div class="section">
      <div class="section-title">📊 基础设置</div>
      <div class="settings-grid">
        <div class="setting-item">
          <label class="setting-label">羊只数量</label>
          <el-input-number
            v-model="localConfig.sheepCount"
            :min="1"
            :max="10000"
            size="small"
            controls-position="right"
            @change="onSheepCountChange"
          />
          <span class="setting-hint">建议 ≥ 200 只</span>
        </div>
      </div>
    </div>

    <!-- 国标参数 -->
    <div class="section">
      <div class="section-title">🐑 国标参数</div>

      <div class="type-display">
        <div class="type-info">
          <span class="type-icon">{{ localConfig.lambType === 'female' ? '🐑' : '🐏' }}</span>
          <span class="type-name">{{ localConfig.lambType === 'female' ? '母羔' : '公羔' }}</span>
          <span class="type-hint">（根据批次性别自动匹配）</span>
        </div>
      </div>

      <div class="settings-row">
        <div class="setting-item half">
          <label class="setting-label">进圈斤数</label>
          <el-input-number
            v-model="localConfig.startWeight"
            :min="1"
            :max="200"
            :step="1"
            :precision="1"
            size="small"
            controls-position="right"
            @change="onStartWeightChange"
          />
          <span class="setting-hint">斤</span>
        </div>
        <div class="setting-item half">
          <label class="setting-label">出栏目标</label>
          <el-input-number
            v-model="localConfig.targetOutWeight"
            :min="1"
            :max="300"
            :step="1"
            :precision="1"
            size="small"
            controls-position="right"
            @change="onTargetOutWeightChange"
          />
          <span class="setting-hint">斤</span>
        </div>
      </div>

      <div v-if="localConfig.startWeight > 0 && localConfig.targetOutWeight > 0" class="calc-results">
        <div class="calc-row">
          <span class="calc-label">预估出栏天数</span>
          <span class="calc-value">{{ localConfig.calculatedDays }} 天</span>
        </div>
        <div class="calc-row">
          <span class="calc-label">预估平均日增重</span>
          <span class="calc-value">{{ localConfig.calculatedA }} 斤/天</span>
        </div>
      </div>

      <!-- 配料参数调节 -->
      <div class="adjustable-params">
        <div class="adjustable-title">⚙️ 配料参数调节</div>
        <div class="adjustable-row">
          <div class="adjustable-item">
            <label class="adjustable-label">蛋白峰值</label>
            <el-input-number
              v-model="localConfig.peakProtein"
              :min="10"
              :max="30"
              :step="0.1"
              size="small"
              controls-position="right"
              @change="onPeakProteinChange"
            />
            <span class="adjustable-unit">%</span>
          </div>
          <div class="adjustable-item">
            <label class="adjustable-label">最大采食量</label>
            <el-input-number
              v-model="localConfig.maxFeedPerMeal"
              :min="0.1"
              :max="10"
              :step="0.1"
              size="small"
              controls-position="right"
              @change="onMaxFeedChange"
            />
            <span class="adjustable-unit">斤/顿</span>
            <el-button
              type="text"
              size="mini"
              class="restore-default-btn"
              @click="restoreMaxFeedDefault"
            >
              恢复默认
            </el-button>
          </div>
          <div class="adjustable-item">
            <label class="adjustable-label">最大采食量对应蛋白</label>
            <el-input-number
              v-model="localConfig.proteinAtMaxFeed"
              :min="10"
              :max="30"
              :step="0.1"
              size="small"
              controls-position="right"
              @change="onProteinAtMaxFeedChange"
            />
            <span class="adjustable-unit">%</span>
          </div>
        </div>

        <div class="adjustable-row" style="margin-top: 10px; border-top: 1px dashed #dcdfe6; padding-top: 10px;">
          <div class="adjustable-item">
            <label class="adjustable-label">高蛋白固定饲料起步量</label>
            <el-input-number
              v-model="localConfig.highProteinFixedStartAmount"
              :min="0"
              :max="10"
              :step="0.01"
              :precision="2"
              size="small"
              controls-position="right"
              @change="onHighProteinFixedStartAmountChange"
            />
            <span class="adjustable-unit">斤/只/天</span>
            <span class="adjustable-hint">（首个固定饲料，蛋白≥18%，默认0.2）</span>
          </div>
          <div class="adjustable-item">
            <label class="adjustable-label">低蛋白固定饲料起步量</label>
            <el-input-number
              v-model="localConfig.lowProteinFixedStartAmount"
              :min="0"
              :max="10"
              :step="0.01"
              :precision="2"
              size="small"
              controls-position="right"
              @change="onLowProteinFixedStartAmountChange"
            />
            <span class="adjustable-unit">斤/只/天</span>
            <span class="adjustable-hint">（首个固定饲料，蛋白&lt;18%，默认0.3）</span>
          </div>
        </div>

        <div class="adjustable-row" style="margin-top: 10px; border-top: 1px dashed #dcdfe6; padding-top: 10px;">
          <div class="adjustable-item">
            <label class="adjustable-label">蛋白最低日</label>
            <el-input-number
              v-model="localConfig.proteinMinDayPercent"
              :min="30"
              :max="95"
              :step="1"
              size="small"
              controls-position="right"
              @change="onProteinMinDayPercentChange"
            />
            <span class="adjustable-unit">%</span>
            <span class="adjustable-hint">（总天数 × 此百分比，默认85%）</span>
          </div>
          <div class="adjustable-item">
            <label class="adjustable-label">采食量峰值日</label>
            <el-input-number
              v-model="localConfig.feedPeakDayPercent"
              :min="30"
              :max="95"
              :step="1"
              size="small"
              controls-position="right"
              @change="onFeedPeakDayPercentChange"
            />
            <span class="adjustable-unit">%</span>
            <span class="adjustable-hint">（总天数 × 此百分比，默认公85% / 母90%）</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 加料粒度 -->
    <div class="section">
      <div class="section-title">⚙️ 加料粒度</div>
      <div class="granularity-radio-group">
        <el-radio-group v-model="localConfig.feedGranularity" size="small" @change="onGranularityChange">
          <el-radio-button label="day">📅 按天加料</el-radio-button>
          <el-radio-button label="meal">⏱️ 按顿加料</el-radio-button>
        </el-radio-group>
        <div class="granularity-hint">
          <span v-if="localConfig.feedGranularity === 'day'">💡 按天：每天早晚各一顿，采食量相同，适合粗略配料</span>
          <span v-else>💡 按顿：每顿采食量独立平滑变化，配料更精准细腻</span>
        </div>
      </div>
    </div>

    <!-- 高蛋白饲料搭配比例 -->
    <div v-if="highProteinFeeds.length > 1" class="section">
      <div class="section-title">
        <span>🧪 高蛋白饲料搭配比例</span>
        <span class="section-count">{{ highProteinFeeds.length }}种</span>
      </div>

      <div class="ratio-tip">
        <span class="ratio-tip-icon">💡</span>
        <div class="ratio-tip-content">
          <span class="ratio-tip-text">设置每种高蛋白饲料在前期和后期的比例</span>
        </div>
      </div>

      <div class="ratio-input-area">
        <div class="ratio-input-header">
          <span class="ratio-header-feed">饲料名称</span>
          <span class="ratio-header-start">前期比例</span>
          <span class="ratio-header-end">后期比例</span>
        </div>
        <div v-for="(feed, index) in highProteinFeeds" :key="index" class="ratio-input-row">
          <div class="ratio-feed-info">
            <span class="ratio-feed-name">{{ feed.name }}</span>
          </div>
          <div class="ratio-input-group">
            <div class="ratio-input-item">
              <el-input
                v-model="highProteinFeeds[index].startRatioDisplay"
                size="small"
                placeholder="0"
                @input="onHighProteinStartRatioChange(index, $event)"
              />
              <span class="ratio-input-unit">份</span>
            </div>
            <span class="ratio-arrow">→</span>
            <div class="ratio-input-item">
              <el-input
                v-model="highProteinFeeds[index].endRatioDisplay"
                size="small"
                placeholder="0"
                @input="onHighProteinEndRatioChange(index, $event)"
              />
              <span class="ratio-input-unit">份</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="highProteinFeeds.length > 0" class="ratio-preview">
        <div class="ratio-preview-item">
          <span class="ratio-preview-label">📌 前期</span>
          <span class="ratio-preview-value">
            <span v-for="(feed, idx) in highProteinFeeds" :key="idx" class="ratio-preview-feed">
              {{ feed.name }}={{ feed.startRatio || '?' }}<span v-if="idx < highProteinFeeds.length - 1" class="ratio-preview-sep"> : </span>
            </span>
          </span>
        </div>
        <div class="ratio-preview-item">
          <span class="ratio-preview-label">🎯 后期</span>
          <span class="ratio-preview-value">
            <span v-for="(feed, idx) in highProteinFeeds" :key="idx" class="ratio-preview-feed">
              {{ feed.name }}={{ feed.endRatio || '?' }}<span v-if="idx < highProteinFeeds.length - 1" class="ratio-preview-sep"> : </span>
            </span>
          </span>
        </div>
      </div>
    </div>

    <!-- 蛋白饲料 -->
    <div class="section">
      <div class="section-title">
        <span>🍚 蛋白饲料</span>
        <span class="section-count">{{ localConfig.proteinFeeds.length }}种</span>
      </div>
      <div class="feed-table">
        <div class="feed-header">
          <span class="col-name">饲料名</span>
          <span class="col-protein">蛋白%</span>
          <span class="col-price">价格(元/斤)</span>
          <span class="col-tons">总用量(吨)</span>
          <span class="col-start">开始天</span>
          <span class="col-end">结束天</span>
        </div>
        <div v-for="(feed, index) in localConfig.proteinFeeds" :key="index" class="feed-row">
          <span class="col-name">{{ feed.name }}</span>
          <span class="col-protein">{{ feed.protein }}</span>
          <span class="col-price">{{ feed.price }}</span>
          <div class="col-tons">
            <el-input
              v-model="feed.totalTonsDisplay"
              size="small"
              placeholder="全程"
              @input="onFeedTotalTonsChange(index, $event)"
              @blur="onFeedBlur(index, 'totalTons')"
            />
          </div>
          <div class="col-start">
            <el-input
              v-model="feed.startDayDisplay"
              size="small"
              placeholder="1"
              @input="onFeedStartDayChange(index, $event)"
              @blur="onFeedBlur(index, 'startDay')"
            />
          </div>
          <div class="col-end">
            <el-input
              v-model="feed.endDayDisplay"
              size="small"
              placeholder="全程"
              :disabled="!feed.totalTons || feed.totalTons <= 0"
              @input="onFeedEndDayChange(index, $event)"
              @blur="onFeedBlur(index, 'endDay')"
            />
            <span v-if="!feed.totalTons || feed.totalTons <= 0" class="end-day-hint">(全程)</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加剂（蛋白=0） -->
    <div class="section">
      <div class="section-title">
        <span>🌿 添加剂</span>
        <span class="section-count">{{ localConfig.additives.length }}种</span>
      </div>
      <div v-if="localConfig.additives.length === 0" class="empty-tip">暂无添加剂</div>
      <div v-for="(additive, index) in localConfig.additives" :key="index" class="additive-item">
        <div class="additive-header">
          <span class="additive-name">{{ additive.name }}</span>
          <span class="additive-price">¥{{ additive.price }}/斤</span>
          <span class="additive-mode">
            <el-radio-group v-model="additive.useMode" size="mini" @change="onAdditiveModeChange(index)">
              <el-radio-button label="full">全程</el-radio-button>
              <el-radio-button label="stage">阶段使用</el-radio-button>
            </el-radio-group>
          </span>
        </div>

        <div class="additive-params">
          <div class="additive-param-item">
            <label class="additive-param-label">起步量</label>
            <el-input-number
              v-model="additive.startAmount"
              :min="0"
              :max="100"
              :step="0.1"
              size="small"
              controls-position="right"
              style="width:100px"
              @change="onAdditiveAmountChange(index)"
            />
            <span class="additive-param-unit">斤/顿</span>
          </div>
          <div class="additive-param-item">
            <label class="additive-param-label">最终量</label>
            <el-input-number
              v-model="additive.endAmount"
              :min="0"
              :max="100"
              :step="0.1"
              size="small"
              controls-position="right"
              style="width:100px"
              @change="onAdditiveAmountChange(index)"
            />
            <span class="additive-param-unit">斤/顿</span>
          </div>
          <span class="additive-param-hint">💡 间隔5天变料一次，自动分配（结果取整）</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TargetGenConfig',
  props: {
    config: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      localConfig: null,
      highProteinFeeds: [],
      _notifyTimer: null,
      _lastStartWeight: null,
      _maxFeedManuallyEdited: false
    }
  },
  watch: {
    config: {
      deep: true,
      handler(newVal) {
        if (newVal && !this._isInternalUpdate) {
          console.log('TargetGenConfig: config changed://///', newVal);

          this.localConfig = newVal
          if (this.localConfig.additives) {
            for (const add of this.localConfig.additives) {
              if (add.startAmount === undefined) add.startAmount = 0
              if (add.endAmount === undefined) add.endAmount = 0
            }
          }
          if (this._lastStartWeight === null) {
            this._lastStartWeight = this.localConfig.startWeight
          }
          this.updateHighProteinFeeds()
        }
      },
      immediate: true
    }
  },
  methods: {
    getDefaultTargetOutWeight(lambType, startWeight) {
      const weight = Number(startWeight) || 0;
      if (lambType === "male") {
        return weight >= 45 ? 120 : 100;
      }
      return weight >= 35 ? 75 : 60;
    },

    getDefaultMaxFeed(lambType, targetOutWeight) {
      const t = Number(targetOutWeight) || 0;
      if (lambType === "male") {
        return Math.round((0.65 + 0.01 * t) * 100) / 100;
      }
      return Math.round((0.9273 + 0.004545 * t) * 100) / 100;
    },

    recalcGrowth() {
      if (!this.localConfig) return;
      const x = Number(this.localConfig.startWeight);
      const targetWeight = Number(this.localConfig.targetOutWeight);
      const isFemale = this.localConfig.lambType === "female";

      if (isNaN(x) || x <= 0 || isNaN(targetWeight) || targetWeight <= 0) {
        this.localConfig.calculatedDays = 0;
        this.localConfig.calculatedA = 0;
        this.localConfig.calculatedB = 0;
        this.localConfig.calculatedAcoef = 0;
        this.localConfig.dailyADG = [];
        this.localConfig.dailyWeight = [];
        return;
      }

      let A, b;
      if (isFemale) {
        A = 0.0056 * x + 0.332;
        b = 0.005 * x + 0.05;
      } else {
        A = 0.004 * x + 0.51;
        b = 0.0072 * x + 0.048;
      }

      const T = (targetWeight - x) / A;
      const days = Math.ceil(T);

      const lnT = Math.log(T);
      const denominator = T * lnT - T + 1;
      const a = (A * T - b * (T - 1)) / denominator;

      const dailyADG = [];
      const dailyWeight = [];
      const dailyResults = [];

      for (let t = 1; t <= days; t++) {
        const lnT_t = Math.log(t);
        const adg = a * lnT_t + b;
        const weight = x + a * (t * lnT_t - t + 1) + b * (t - 1);

        dailyADG.push(Math.round(adg * 1000) / 1000);
        dailyWeight.push(Math.round(weight * 100) / 100);
        dailyResults.push({
          day: t,
          adg: Math.round(adg * 1000) / 1000,
          weight: Math.round(weight * 100) / 100,
        });
      }

      this.localConfig.calculatedDays = days;
      this.localConfig.calculatedA = Math.round(A * 1000) / 1000;
      this.localConfig.calculatedB = Math.round(b * 1000) / 1000;
      this.localConfig.calculatedAcoef = Math.round(a * 10000) / 10000;
      this.localConfig.dailyADG = dailyADG;
      this.localConfig.dailyWeight = dailyWeight;
      this.localConfig.dailyResults = dailyResults;
    },

    recalcMaxFeedIfNotEdited() {
      if (!this.localConfig) return;
      if (this._maxFeedManuallyEdited) return;
      const defaultMaxFeed = this.getDefaultMaxFeed(
        this.localConfig.lambType,
        this.localConfig.targetOutWeight
      );
      this.localConfig.maxFeedPerMeal = defaultMaxFeed;
    },

    onStartWeightChange(value) {
      this.localConfig.startWeight = value || 0;
      if (this.localConfig.lambType) {
        const defaultTarget = this.getDefaultTargetOutWeight(
          this.localConfig.lambType,
          this.localConfig.startWeight
        );
        const oldDefault = this.getDefaultTargetOutWeight(
          this.localConfig.lambType,
          this._lastStartWeight || this.localConfig.startWeight
        );
        if (
          !this.localConfig.targetOutWeight ||
          this.localConfig.targetOutWeight === oldDefault
        ) {
          this.localConfig.targetOutWeight = defaultTarget;
        }
      }
      this._lastStartWeight = this.localConfig.startWeight;
      this.recalcGrowth();
      this.recalcMaxFeedIfNotEdited();
      this.notifyChange();
    },

    onTargetOutWeightChange(value) {
      this.localConfig.targetOutWeight = value || 0;
      this.recalcGrowth();
      this.recalcMaxFeedIfNotEdited();
      this.notifyChange();
    },

    onMaxFeedChange(value) {
      this.localConfig.maxFeedPerMeal = value || 0;
      this._maxFeedManuallyEdited = true;
      this.notifyChange();
    },

    restoreMaxFeedDefault() {
      this._maxFeedManuallyEdited = false;
      const defaultMaxFeed = this.getDefaultMaxFeed(
        this.localConfig.lambType,
        this.localConfig.targetOutWeight
      );
      this.localConfig.maxFeedPerMeal = defaultMaxFeed;
      this.$message.success(`已恢复默认最大采食量：${defaultMaxFeed} 斤/顿`);
      this.notifyChange();
    },

    onProteinMinDayPercentChange(value) {
      this.localConfig.proteinMinDayPercent = value || 80
      this.notifyChange()
    },

    onFeedPeakDayPercentChange(value) {
      this.localConfig.feedPeakDayPercent = value || 85
      this.notifyChange()
    },

    onHighProteinFixedStartAmountChange(value) {
      this.localConfig.highProteinFixedStartAmount = value || 0
      this.notifyChange()
    },

    onLowProteinFixedStartAmountChange(value) {
      this.localConfig.lowProteinFixedStartAmount = value || 0
      this.notifyChange()
    },

    calculateAdditivePhases(startAmount, endAmount, totalDays) {
      const INTERVAL_DAYS = 5

      if (totalDays <= 0) return []
      if (startAmount === 0 && endAmount === 0) return []

      const phaseCount = Math.ceil(totalDays / INTERVAL_DAYS)

      if (phaseCount <= 1) {
        return [{
          startDay: 1,
          endDay: totalDays,
          amount: Math.round(startAmount)
        }]
      }

      const step = (endAmount - startAmount) / (phaseCount - 1)
      const phases = []

      for (let i = 0; i < phaseCount; i++) {
        const amount = Math.round(startAmount + i * step)
        const startDay = i * INTERVAL_DAYS + 1
        const endDay = Math.min((i + 1) * INTERVAL_DAYS, totalDays)
        phases.push({
          startDay: startDay,
          endDay: endDay,
          amount: amount
        })
      }

      return phases
    },

    getAdditivePreview(index) {
      const add = this.localConfig.additives[index]
      const totalDays = this.localConfig.calculatedDays || 60

      if (!add.startAmount && !add.endAmount) return '请设置起步量和最终量'

      const phases = this.calculateAdditivePhases(add.startAmount, add.endAmount, totalDays)
      if (phases.length === 0) return '无数据'

      return phases.map(p => `第${p.startDay}~${p.endDay}天:${p.amount}斤`).join(' → ')
    },

    onAdditiveAmountChange(index) {
      this.notifyChange()
    },

    onPeakProteinChange(value) {
      this.localConfig.peakProtein = value || 0
      this.notifyChange()
    },

    onProteinAtMaxFeedChange(value) {
      this.localConfig.proteinAtMaxFeed = value || 0
      this.notifyChange()
    },

    updateHighProteinFeeds() {
      if (!this.localConfig) return
      const proteinFeeds = this.localConfig.proteinFeeds || []
      const startRatios = this.localConfig.highProteinStartRatios || {}
      const endRatios = this.localConfig.highProteinEndRatios || {}

      this.highProteinFeeds = proteinFeeds
        .filter(f => {
          const protein = parseFloat(f.protein) || 0
          const isFixed = f.endDay !== null && f.endDay !== undefined && f.endDay > 0
          return protein >= 30 && !isFixed
        })
        .map(f => ({
          ...f,
          startRatio: startRatios[f.name] !== undefined ? startRatios[f.name] : 0,
          endRatio: endRatios[f.name] !== undefined ? endRatios[f.name] : 0,
          startRatioDisplay: startRatios[f.name] !== undefined ? String(startRatios[f.name]) : '',
          endRatioDisplay: endRatios[f.name] !== undefined ? String(endRatios[f.name]) : ''
        }))
    },

    onAdditiveModeChange(index) {
      this.notifyChange()
    },

    onGranularityChange(value) {
      this.$message.success(value === 'day' ? '已切换为按天加料' : '已切换为按顿加料')
      this.notifyChange()
    },

    onHighProteinStartRatioChange(index, value) {
      const cleaned = String(value).replace(/[^0-9.]/g, '')
      const num = parseFloat(cleaned) || 0
      this.highProteinFeeds[index].startRatio = num
      this.highProteinFeeds[index].startRatioDisplay = cleaned

      const startRatios = {}
      for (const f of this.highProteinFeeds) {
        if (f.startRatio > 0) startRatios[f.name] = f.startRatio
      }
      this.localConfig.highProteinStartRatios = startRatios
      this.notifyChange()
    },

    onHighProteinEndRatioChange(index, value) {
      const cleaned = String(value).replace(/[^0-9.]/g, '')
      const num = parseFloat(cleaned) || 0
      this.highProteinFeeds[index].endRatio = num
      this.highProteinFeeds[index].endRatioDisplay = cleaned

      const endRatios = {}
      for (const f of this.highProteinFeeds) {
        if (f.endRatio > 0) endRatios[f.name] = f.endRatio
      }
      this.localConfig.highProteinEndRatios = endRatios
      this.notifyChange()
    },

    onSheepCountChange(value) {
      this.localConfig.sheepCount = value || 0
      this.notifyChange()
    },

    onFeedTotalTonsChange(index, value) {
      const cleaned = String(value).replace(/[^0-9.]/g, '')
      const num = parseFloat(cleaned) || null
      const feed = this.localConfig.proteinFeeds[index]

      feed.totalTons = num
      feed.totalTonsDisplay = cleaned

      if (num && num > 0) {
        if (!feed.endDay) {
          feed.endDay = this.localConfig.calculatedDays || 60
          feed.endDayDisplay = String(this.localConfig.calculatedDays || 60)
        }
      } else {
        feed.endDay = null
        feed.endDayDisplay = ''
      }

      this.updateHighProteinFeeds()
      this.notifyChange()
    },

    onFeedStartDayChange(index, value) {
      const cleaned = String(value).replace(/[^0-9]/g, '')
      const num = parseInt(cleaned) || null
      const feed = this.localConfig.proteinFeeds[index]
      feed.startDay = num
      feed.startDayDisplay = cleaned
      this.notifyChange()
    },

    onFeedEndDayChange(index, value) {
      const cleaned = String(value).replace(/[^0-9]/g, '')
      const num = parseInt(cleaned) || null
      const feed = this.localConfig.proteinFeeds[index]
      feed.endDay = num
      feed.endDayDisplay = cleaned
      this.updateHighProteinFeeds()
      this.notifyChange()
    },

    onFeedBlur(index, field) {
      const feed = this.localConfig.proteinFeeds[index]
      if (field === 'totalTons' && (feed.totalTonsDisplay === '' || feed.totalTonsDisplay === null)) {
        feed.totalTons = null
        feed.endDay = null
        feed.endDayDisplay = ''
      }
      if (field === 'startDay' && feed.startDayDisplay === '') {
        feed.startDay = null
      }
      if (field === 'endDay' && feed.endDayDisplay === '') {
        feed.endDay = null
      }
    },

    notifyChange() {
      if (this._notifyTimer) clearTimeout(this._notifyTimer)
      this._notifyTimer = setTimeout(() => {
        this._isInternalUpdate = true
        this.$emit('update:config', this.localConfig)
        this.$emit('configChange', this.localConfig)
        setTimeout(() => { this._isInternalUpdate = false }, 50)
      }, 300)
    }
  }
}
</script>

<style scoped>
.target-gen-config {
  padding: 4px 0;
}

.section {
  margin-bottom: 16px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
}

.section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #f5f7fa;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.section-count {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
  background: #e4e7ed;
  padding: 0 8px;
  border-radius: 10px;
}

.settings-grid {
  padding: 14px;
}

.setting-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.setting-label {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}

.setting-hint {
  font-size: 12px;
  color: #909399;
}

.type-display {
  padding: 0 14px 10px;
}

.type-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #f0f9eb;
  border-radius: 4px;
}

.type-icon {
  font-size: 18px;
}

.type-name {
  font-size: 14px;
  font-weight: 500;
  color: #67c23a;
}

.type-hint {
  font-size: 12px;
  color: #909399;
}

.settings-row {
  display: flex;
  gap: 20px;
  padding: 0 14px 10px;
}

.setting-item.half {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.setting-display {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.calc-results {
  padding: 0 14px 14px;
  display: flex;
  gap: 30px;
}

.calc-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.calc-label {
  font-size: 13px;
  color: #606266;
}

.calc-value {
  font-size: 14px;
  font-weight: 600;
  color: #409eff;
}

.granularity-radio-group {
  padding: 14px;
}

.granularity-radio-group .el-radio-group {
  display: flex;
  gap: 0;
}

.granularity-radio-group .el-radio-button {
  margin-right: 0;
}

.granularity-radio-group .el-radio-button:first-child .el-radio-button__inner {
  border-radius: 4px 0 0 4px;
}

.granularity-radio-group .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 0 4px 4px 0;
}

.granularity-hint {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}

.ratio-tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 14px;
  background: #f0f9eb;
  margin: 0 14px 10px;
  border-radius: 4px;
}

.ratio-tip-icon {
  font-size: 16px;
}

.ratio-tip-text {
  font-size: 13px;
  color: #67c23a;
}

.ratio-input-area {
  padding: 0 14px 14px;
}

.ratio-input-header {
  display: flex;
  padding: 6px 0;
  border-bottom: 1px solid #ebeef5;
  font-size: 12px;
  color: #909399;
}

.ratio-header-feed {
  width: 120px;
}

.ratio-header-start,
.ratio-header-end {
  width: 100px;
  text-align: center;
}

.ratio-input-row {
  display: flex;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f5f7fa;
}

.ratio-feed-info {
  width: 120px;
  font-size: 13px;
  color: #303133;
}

.ratio-feed-name {
  font-weight: 500;
}

.ratio-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ratio-input-item {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 90px;
}

.ratio-input-unit {
  font-size: 12px;
  color: #909399;
}

.ratio-arrow {
  color: #909399;
  font-size: 14px;
}

.ratio-preview {
  padding: 0 14px 14px;
  background: #fafafa;
  border-radius: 4px;
  margin: 0 14px 14px;
}

.ratio-preview-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
}

.ratio-preview-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.ratio-preview-value {
  font-size: 13px;
  color: #303133;
}

.ratio-preview-feed {
  background: #ecf5ff;
  padding: 0 6px;
  border-radius: 3px;
}

.ratio-preview-sep {
  color: #909399;
  margin: 0 2px;
}

.feed-table {
  padding: 0 14px 14px;
}

.feed-header {
  display: flex;
  padding: 6px 0;
  border-bottom: 1px solid #ebeef5;
  font-size: 12px;
  color: #909399;
}

.feed-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f5f7fa;
}

.col-name {
  width: 100px;
  font-size: 13px;
  color: #303133;
  flex-shrink: 0;
}

.col-protein {
  width: 60px;
  font-size: 13px;
  color: #606266;
  flex-shrink: 0;
}

.col-price {
  width: 90px;
  font-size: 13px;
  color: #606266;
  flex-shrink: 0;
}

.col-tons {
  width: 100px;
  padding-right: 8px;
  flex-shrink: 0;
}

.col-start {
  width: 70px;
  padding-right: 8px;
  flex-shrink: 0;
}

.col-end {
  width: 100px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.end-day-hint {
  font-size: 12px;
  color: #909399;
  padding-left: 4px;
  width: 50px;
}

.additive-item {
  padding: 0 14px 14px;
  border-bottom: 1px solid #f5f7fa;
}

.additive-item:last-child {
  border-bottom: none;
}

.additive-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  gap: 12px;
}

.additive-mode {
  flex-shrink: 0;
}

.additive-mode :deep(.el-radio-button__inner) {
  padding: 4px 10px;
  font-size: 12px;
}

.additive-name {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
}

.additive-price {
  font-size: 12px;
  color: #909399;
}

.additive-params {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 0 8px 0;
  flex-wrap: wrap;
}

.additive-param-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.additive-param-label {
  font-size: 13px;
  color: #606266;
}

.additive-param-unit {
  font-size: 12px;
  color: #909399;
}

.additive-param-hint {
  font-size: 12px;
  color: #909399;
}

.additive-preview {
  padding: 6px 0 4px 0;
  font-size: 12px;
  color: #606266;
  background: #f5f7fa;
  border-radius: 4px;
  padding: 6px 10px;
}

.additive-preview-label {
  color: #909399;
}

.additive-preview-value {
  color: #303133;
}

.adjustable-params {
  padding: 10px 14px 14px;
  border-top: 1px solid #ebeef5;
  margin-top: 4px;
}

.adjustable-title {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 10px;
}

.adjustable-row {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.adjustable-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.adjustable-label {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}

.adjustable-unit {
  font-size: 12px;
  color: #909399;
}

.adjustable-item :deep(.el-input-number) {
  width: 100px;
}

.adjustable-item :deep(.el-input-number .el-input__inner) {
  text-align: center;
}

.restore-default-btn {
  margin-left: 4px;
  color: #409eff;
  font-size: 12px;
}

:deep(.el-input-number) {
  width: 120px;
}

:deep(.el-input-number .el-input__inner) {
  text-align: center;
}

:deep(.el-input--small .el-input__inner) {
  height: 28px;
  line-height: 28px;
}

.empty-tip {
  padding: 14px;
  text-align: center;
  color: #909399;
  font-size: 13px;
}

.adjustable-hint {
  font-size: 11px;
  color: #909399;
  margin-left: 4px;
}
</style>